import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Upload, CheckCircle, XCircle, Image as ImageIcon, AlertTriangle, Info, Settings } from 'lucide-react';
import { useCloudinaryUpload } from '@/hooks/use-cloudinary-upload';
import { SupplierInfo, ProductInfo, CloudinaryUploadResult } from '@/lib/cloudinary-upload-service';

export function CloudinaryUploadDemo() {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [supplierInfo, setSupplierInfo] = useState<SupplierInfo>({
    id: 'demo-supplier-123',
    name: 'Acme Supplies & Co.',
    email: 'contact@acmesupplies.com'
  });
  const [productInfo, setProductInfo] = useState<ProductInfo>({
    id: 'demo-product-456',
    name: 'Heavy Duty Excavator',
    slug: 'heavy-duty-excavator-2024'
  });

  const {
    uploadMultipleImages,
    uploadWithProgress,
    validateFiles,
    getPreviewStructure,
    isUploading,
    uploadProgress,
    uploadedCount,
    totalCount,
    currentFile,
    error,
    results,
    configValid,
    configErrors,
    resetState
  } = useCloudinaryUpload({
    onProgress: (progress, completed, total, currentFileName) => {
      console.log(`Upload progress: ${progress}% (${completed}/${total}) - Current: ${currentFileName || 'N/A'}`);
    },
    onSuccess: (uploadResults) => {
      console.log('All uploads completed successfully:', uploadResults);
    },
    onError: (errorMessage) => {
      console.error('Upload error:', errorMessage);
    },
    onFileValidationError: (file, error) => {
      console.warn(`File validation failed for ${file.name}:`, error);
    },
    uploadOptions: {
      maxRetries: 3,
      retryDelay: 1000,
      quality: 'auto',
      format: 'auto'
    }
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    resetState();
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    try {
      await uploadWithProgress(selectedFiles, supplierInfo, productInfo);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleQuickUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    try {
      await uploadMultipleImages(selectedFiles, supplierInfo, productInfo);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Validate selected files
  const fileValidation = selectedFiles.length > 0 ? validateFiles(selectedFiles) : { valid: [], invalid: [] };
  const folderStructure = getPreviewStructure(supplierInfo, productInfo);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Enhanced Cloudinary Upload Demo
          </CardTitle>
          <div className="text-sm text-muted-foreground">
            Complete demonstration of Cloudinary image upload with supplier folder organization
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Configuration Status */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Configuration Status
            </Label>
            {configValid ? (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  ✅ Cloudinary configuration is valid and ready for uploads
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  ❌ Configuration issues: {configErrors.join(', ')}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <Separator />

          {/* Supplier Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="supplier-name">Supplier Name</Label>
              <Input
                id="supplier-name"
                value={supplierInfo.name}
                onChange={(e) => setSupplierInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter supplier name"
              />
            </div>
            <div>
              <Label htmlFor="supplier-id">Supplier ID</Label>
              <Input
                id="supplier-id"
                value={supplierInfo.id}
                onChange={(e) => setSupplierInfo(prev => ({ ...prev, id: e.target.value }))}
                placeholder="Enter supplier ID"
              />
            </div>
          </div>

          {/* Product Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="product-name">Product Name</Label>
              <Input
                id="product-name"
                value={productInfo.name}
                onChange={(e) => setProductInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter product name"
              />
            </div>
            <div>
              <Label htmlFor="product-slug">Product Slug</Label>
              <Input
                id="product-slug"
                value={productInfo.slug || ''}
                onChange={(e) => setProductInfo(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="Enter product slug"
              />
            </div>
          </div>

          {/* Folder Structure Preview */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Info className="h-4 w-4" />
                Expected Folder Structure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-mono space-y-1">
                <div>📁 suppliers/</div>
                <div className="ml-4">📁 {folderStructure.supplierFolder}/</div>
                {productInfo.slug && (
                  <div className="ml-8">📁 {productInfo.slug}/</div>
                )}
                <div className="ml-12">🖼️ {Date.now()}_image_name.jpg</div>
              </div>
              <div className="mt-2 text-xs text-blue-600">
                <strong>Full Path:</strong> {folderStructure.fullPath}
              </div>
            </CardContent>
          </Card>

          {/* File Selection */}
          <div>
            <Label htmlFor="file-upload">Select Images</Label>
            <Input
              id="file-upload"
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="mt-2"
            />
            {selectedFiles.length > 0 && (
              <div className="mt-2 text-sm text-gray-600">
                {selectedFiles.length} file(s) selected
              </div>
            )}
          </div>

          {/* File Validation Results */}
          {selectedFiles.length > 0 && (
            <div className="space-y-4">
              {/* Valid Files */}
              {fileValidation.valid.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-green-600">✅ Valid Files ({fileValidation.valid.length})</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {fileValidation.valid.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 border border-green-200 bg-green-50 rounded">
                        <ImageIcon className="h-4 w-4 text-green-600" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{file.name}</div>
                          <div className="text-xs text-green-600">{formatFileSize(file.size)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invalid Files */}
              {fileValidation.invalid.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-red-600">❌ Invalid Files ({fileValidation.invalid.length})</Label>
                  <div className="space-y-2">
                    {fileValidation.invalid.map(({ file, error }, index) => (
                      <div key={index} className="flex items-start gap-2 p-2 border border-red-200 bg-red-50 rounded">
                        <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{file.name}</div>
                          <div className="text-xs text-red-600">{error}</div>
                          <div className="text-xs text-gray-500">{formatFileSize(file.size)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upload Buttons */}
          <div className="flex gap-4">
            <Button 
              onClick={handleUpload} 
              disabled={isUploading || selectedFiles.length === 0 || !configValid}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Upload with Progress
            </Button>
            <Button 
              onClick={handleQuickUpload} 
              disabled={isUploading || selectedFiles.length === 0 || !configValid}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Quick Upload (Parallel)
            </Button>
            <Button 
              onClick={resetState} 
              variant="ghost"
              disabled={isUploading}
            >
              Reset
            </Button>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-3 bg-blue-50 border border-blue-200 rounded p-4">
              <div className="flex justify-between text-sm font-medium">
                <span>📤 Uploading images to Cloudinary...</span>
                <span>{uploadedCount}/{totalCount} completed</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
              <div className="text-xs text-blue-600 space-y-1">
                <div className="text-center">{uploadProgress.toFixed(1)}% complete</div>
                {currentFile && (
                  <div className="text-center">Currently uploading: <span className="font-mono">{currentFile}</span></div>
                )}
                <div className="text-center">Images organized by supplier folder: {folderStructure.fullPath}</div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Upload Results */}
          {results.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Upload Results:</Label>
                <div className="text-sm text-muted-foreground">
                  {results.filter(r => r.uploadStatus === 'Success').length} successful, {results.filter(r => r.uploadStatus !== 'Success').length} failed
                </div>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {results.map((result, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      {/* Status Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {result.uploadStatus === 'Success' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          <Badge variant={result.uploadStatus === 'Success' ? 'default' : 'destructive'}>
                            {result.uploadStatus}
                          </Badge>
                        </div>
                        {result.uploadedAt && (
                          <div className="text-xs text-muted-foreground">
                            {new Date(result.uploadedAt).toLocaleTimeString()}
                          </div>
                        )}
                      </div>
                      
                      {/* Image Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <strong>Supplier Folder:</strong> {result.supplierName}
                        </div>
                        {result.folderPath && (
                          <div>
                            <strong>Full Path:</strong> {result.folderPath}
                          </div>
                        )}
                        {result.width && result.height && (
                          <div>
                            <strong>Dimensions:</strong> {result.width}x{result.height}
                          </div>
                        )}
                        {result.format && (
                          <div>
                            <strong>Format:</strong> {result.format.toUpperCase()}
                          </div>
                        )}
                        {result.bytes && (
                          <div>
                            <strong>Size:</strong> {formatFileSize(result.bytes)}
                          </div>
                        )}
                        {result.publicId && (
                          <div className="md:col-span-2">
                            <strong>Public ID:</strong>
                            <div className="text-xs font-mono bg-gray-100 p-1 rounded mt-1 break-all">
                              {result.publicId}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Tags */}
                      {result.tags && result.tags.length > 0 && (
                        <div>
                          <strong className="text-sm">Tags:</strong>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {result.tags.map((tag, tagIndex) => (
                              <Badge key={tagIndex} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Image URL and Actions */}
                      {result.imageUrl && (
                        <div className="space-y-2">
                          <div>
                            <strong>Image URL:</strong>
                            <div className="text-xs font-mono bg-gray-100 p-2 rounded break-all">
                              {result.imageUrl}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(result.imageUrl, '_blank')}
                            >
                              View Image
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigator.clipboard.writeText(result.imageUrl)}
                            >
                              Copy URL
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Error Details */}
                      {result.error && (
                        <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
                          <strong>Error:</strong> {result.error}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* JSON Output */}
          {results.length > 0 && (
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-sm">JSON Output (for API integration)</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-white p-3 rounded border overflow-auto max-h-64">
                  {JSON.stringify(results, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 